import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { MessageCircle, ThumbsUp, Video, BookOpen, Share2, ExternalLink, FileText, MessageSquare } from 'lucide-react';
import { Topic } from '../types';
import * as Icons from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import Modal from './Modal';
import ContentActions from './common/ContentActions';
import Card from './common/Card';
import Avatar from './common/Avatar';

interface TopicCardProps {
  topic: Topic;
  onLike?: () => void;
  onShare?: () => void;
}

export default function TopicCard({ topic, onLike, onShare }: TopicCardProps) {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Default icon based on content type
  const defaultIcon = {
    article: FileText,
    video: Video,
    post: MessageSquare
  }[topic.type];

  // Get category icon, fallback to default if not found
  const CategoryIcon = topic.category.icon ? 
    (Icons[topic.category.icon as keyof typeof Icons] || defaultIcon) : 
    defaultIcon;

  const handleLike = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onLike) {
      onLike();
    }
  };

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onShare) {
      onShare();
    } else {
      try {
        await navigator.share({
          title: topic.title,
          text: topic.description,
          url: `${window.location.origin}/${topic.type}/${topic.id}`,
        });
      } catch (err) {
        navigator.clipboard.writeText(`${window.location.origin}/${topic.type}/${topic.id}`);
        alert('Link copied to clipboard!');
      }
    }
  };

  const handleCardClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsModalOpen(true);
  };

  const handleViewFull = () => {
    setIsModalOpen(false);
    navigate(`/${topic.type}/${topic.id}`);
  };

  return (
    <>
      <Card onClick={handleCardClick}>
        {topic.thumbnailUrl && (
          <img 
            src={topic.thumbnailUrl} 
            alt={topic.title}
            className="w-full h-48 object-cover"
          />
        )}
        <div className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Avatar
              src={topic.author.avatar}
              alt={topic.author.name}
              size="md"
            />
            <div>
              <p className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                {topic.author.name}
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatDistanceToNow(topic.createdAt, { addSuffix: true })}
              </p>
            </div>
          </div>
          
          <h2 className={`text-xl font-semibold mb-2 group-hover:text-blue-600 transition-colors
            ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}
          `}>
            {topic.title}
          </h2>
          
          <p className={`mb-4 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
            {topic.description}
          </p>
          
          <div className="flex items-center justify-between">
            <ContentActions
              content={topic}
              onLike={handleLike}
              onComment={() => navigate(`/${topic.type}/${topic.id}#comments`)}
              onShare={handleShare}
              compact
            />
            
            <div className="flex items-center space-x-2 text-blue-600">
              {CategoryIcon && <CategoryIcon className="h-4 w-4" />}
              <span className="text-sm font-medium">{topic.category.name}</span>
            </div>
          </div>
        </div>
      </Card>

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={`${topic.type.charAt(0).toUpperCase() + topic.type.slice(1)} Preview`}
      >
        <div className="space-y-6">
          {topic.thumbnailUrl && (
            <img 
              src={topic.thumbnailUrl} 
              alt={topic.title}
              className="w-full h-64 object-cover rounded-lg"
            />
          )}

          <div className="flex items-center space-x-4">
            <Avatar
              src={topic.author.avatar}
              alt={topic.author.name}
              size="lg"
            />
            <div>
              <p className={`font-medium ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>
                {topic.author.name}
              </p>
              <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                {formatDistanceToNow(topic.createdAt, { addSuffix: true })}
              </p>
            </div>
          </div>

          <div>
            <h2 className={`text-2xl font-bold mb-4 ${
              theme === 'dark' ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {topic.title}
            </h2>
            
            <p className={`mb-6 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
              {topic.description}
            </p>

            {topic.content && (
              <div className={`mb-6 prose ${
                theme === 'dark' ? 'prose-invert' : ''
              }`}>
                {topic.content}
              </div>
            )}

            {topic.tags && topic.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {topic.tags.map(tag => (
                  <Link
                    key={tag}
                    to={`/tag/${tag}`}
                    className={`
                      text-sm px-3 py-1 rounded-full transition-colors
                      ${theme === 'dark'
                        ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }
                    `}
                  >
                    #{tag}
                  </Link>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
              <ContentActions
                content={topic}
                onLike={handleLike}
                onComment={() => {
                  setIsModalOpen(false);
                  navigate(`/${topic.type}/${topic.id}#comments`);
                }}
                onShare={handleShare}
              />

              <button
                onClick={handleViewFull}
                className={`
                  flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors
                  ${theme === 'dark'
                    ? 'bg-gray-700 text-gray-100 hover:bg-gray-600'
                    : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }
                `}
              >
                <ExternalLink className="h-4 w-4" />
                <span>View Full {topic.type}</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
}